import ErrorIcon from "@assets/icons/error.svg";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import styled from "@emotion/styled";
import { BackgroundIcon } from "@ui/components/common/background-icon";
import { Button, RefreshButton } from "@ui/components/buttons/button";
import { RoutesPath } from "@ui/navigation/routes";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import React, { CSSProperties, HTMLAttributes, useCallback } from "react";

export const Error404: React.FC = () => {
	const { format } = useI18n();
	const navigate = useNavigate();
	return (
		<Container>
			<BackgroundIcon iconSvg={ErrorIcon} />
			<Code>{format("errorPage.404.code")}</Code>
			<Title>{format("errorPage.404.title")}</Title>
			<Description className="p-size-m">{format("errorPage.404.description")}</Description>
			<Button
				style={{ marginTop: 16 }}
				label={format("errorPage.404.button")}
				onClick={() => navigate(RoutesPath.Home)}
			/>
		</Container>
	);
};

export const NetworkError: React.FC<
	{
		title?: string;
		titleStyle?: CSSProperties;
		description?: string;
		icon?: JSX.Element;
		withoutIcon?: boolean;
		onRefresh?: () => Promise<void> | void;
	} & HTMLAttributes<HTMLDivElement>
> = ({ title, titleStyle, description, icon, withoutIcon, onRefresh, ...otherProps }) => {
	const { format } = useI18n();
	const reload = useCallback(async () => {
		if (onRefresh) {
			await onRefresh();
		} else {
			location.reload();
		}
	}, [onRefresh]);
	const [operation, loading] = useAsyncOperation(reload, {});

	return (
		<Container {...otherProps}>
			{withoutIcon ? null : (icon ?? <BackgroundIcon iconSvg={ErrorIcon} />)}
			<Title style={titleStyle}>{title ?? format("errorPage.network.title")}</Title>
			<Description className="p-size-m">{description ?? format("errorPage.network.description")}</Description>
			<RefreshButton style={{ marginTop: 16 }} loading={loading} onClick={operation} />
		</Container>
	);
};

const Container = styled.div`
	${stack(16, "center", "center")}
`;

const Title = styled.h1`
	text-align: center;
	color: ${colors.grey};
	margin-top: 16px;
`;

const Code = styled.div`
	text-align: center;
	margin-top: 16px;
	color: ${colors.grey};
`;

const Description = styled.div`
	text-align: center;
	max-width: 448px;
	color: ${colors.grey};
`;
